import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  HStack,
  VStack,
  Spinner,
  Divider,
  Image,
  Text,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useOutsideClick,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import api from '../api';
import CategoryFilter from '../components/CategoryFilter';
import CreateProduct from '../components/CreateProduct';
import ProductSidebar from '../components/ProductSidebar';
import usePagination from '../utils/usePagination';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImagePopupOpen, setIsImagePopupOpen] = useState(false);
  const [isCreateProductOpen, setCreateProductOpen] = useState(false);
  const [isEditProductOpen, setEditProductOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState(null);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    selectedCategories: [],
    minPrice: '',
    maxPrice: '',
    isVat: false,
  });
  const dropdownRef = useRef();
  const placeholderImage = '/placeholder-product.jpeg';
  

  const fetchProducts = async (url = '/products/') => {
    setLoading(true);

    const params = new URLSearchParams();
    if (filters.name) params.append('search', filters.name);
    if (filters.minPrice) params.append('min_price', filters.minPrice);
    if (filters.maxPrice) params.append('max_price', filters.maxPrice);
    if (filters.isVat) params.append('is_vat', filters.isVat);
    if (filters.selectedCategories.length) {
      params.append(
        'categories',
        filters.selectedCategories.map((cat) => cat.id).join(',')
      );
    }

    const fullUrl = `${url}${params.toString() ? `?${params.toString()}` : ''}`.replace('??', '?');

    try {
      const response = await api.get(fullUrl);
      setProducts(response.data.results);
      updatePagination(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const { pagination, updatePagination, fetchNext, fetchPrevious, goToPage } = usePagination(fetchProducts, '/products/');

  useEffect(() => {
    fetchProducts();
  }, [filters]);

  const handleOpenCreateProduct = () => setCreateProductOpen(true);
  const handleCloseCreateProduct = () => setCreateProductOpen(false);

  const handleOpenEditProduct = async (productId, event) => {
    event.stopPropagation();
    try {
      const response = await api.get(`/products/${productId}/`);
      const product = response.data;
  
      // Fetch the full category hierarchy
      const resolveCategories = async (categoryId) => {
        if (!categoryId) return [];
        const categoryResponse = await api.get(`/categories/${categoryId}/`);
        const category = categoryResponse.data;
        const parentPath = await resolveCategories(category.parent);
        return [...parentPath, category];
      };
  
      const fullCategoryPath = await resolveCategories(product.categories[product.categories.length - 1]);
  
      // Set the product for editing
      setProductToEdit({
        id: product.id,
        name: product.name,
        description: product.description,
        price: product.price,
        purchase_price: product.purchase_price,
        markup_percentage: product.markup_percentage,
        stock_quantity: product.stock_quantity,
        catalog_number: product.catalog_number,
        unit_measurement: product.unit_measurement,
        is_vat: product.is_vat,
        categories: fullCategoryPath.map((cat) => cat.id),
        country: product.country,
        images: product.images.map((image, index) => ({
          file: null, // Backend images won't have a file
          preview: image.image, // Image URL from the backend
          id: image.id,
          priority: image.priority || index + 1,
        })),
      });
  
      setEditProductOpen(true);
    } catch (error) {
      console.error('Failed to fetch product data for editing:', error);
    }
  };

  const handleCategoryFilterChange = (updatedCategories) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      selectedCategories: updatedCategories.filter((cat) => !cat.isIndeterminate) // Exclude indeterminate states
    }));
  };

  const handleCloseEditProduct = () => {
    setProductToEdit(null);
    setEditProductOpen(false);
  };

  const handleOpenDeleteConfirm = (product, event) => {
    event.stopPropagation();
    setProductToDelete(product);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => setDeleteConfirmOpen(false);

  const handleProductCreated = () => {
    fetchProducts();
    handleCloseCreateProduct();
  };

  const handleProductUpdated = () => {
    fetchProducts();
    handleCloseEditProduct();
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      await api.delete(`/products/${productToDelete.id}/`);
      fetchProducts();
    } catch (error) {
      console.error('Failed to delete product:', error);
    } finally {
      handleCloseDeleteConfirm();
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setCurrentImageIndex(0);
  };

  const handleCloseSidebar = () => setSelectedProduct(null);

  const toggleCategoryFilter = () => setShowCategoryFilter((prev) => !prev);

  const clearFilters = () => {
    setFilters({
      name: '',
      selectedCategories: [],
      minPrice: '',
      maxPrice: '',
      isVat: false,
    });
  };

  const openImagePopup = () => setIsImagePopupOpen(true);
  const closeImagePopup = () => setIsImagePopupOpen(false);

  useOutsideClick({
    ref: dropdownRef,
    handler: () => setShowCategoryFilter(false),
  });

  return (
    <Box p="6">
      {/* Header */}
      <Flex justify="space-between" align="center" mb="6">
        <Heading as="h2" size="lg">
          Товари
        </Heading>
        <Button colorScheme="blue" leftIcon={<AddIcon />} onClick={handleOpenCreateProduct}>
          Створити новий товар
        </Button>
        <CreateProduct
          isOpen={isCreateProductOpen}
          onClose={handleCloseCreateProduct}
          onProductCreated={handleProductCreated}
        />
        {isEditProductOpen && productToEdit && (
          <CreateProduct
            isOpen={isEditProductOpen}
            onClose={handleCloseEditProduct}
            onProductCreated={handleProductUpdated}
            initialData={productToEdit} // Pre-filled data for editing
          />
        )}
      </Flex>

      {/* Filters Section */}
      <Box bg="gray.50" p="4" mb="6" borderRadius="md" boxShadow="sm">
        <VStack spacing="4" align="flex-start" w="full">
          {/* Search Field */}
          <HStack spacing="4" w="full">
            <Input
              placeholder="Пошук"
              name="name"
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
              flex="1"
            />
          </HStack>

          {/* Category and Price Filters */}
          <HStack spacing="2" w="full">
            <Button onClick={toggleCategoryFilter} colorScheme="blue" variant="outline">
              {showCategoryFilter ? 'Заховати категорії' : 'Показати категорії'}
            </Button>
            {showCategoryFilter && (
              <Box
                ref={dropdownRef}
                position="absolute"
                zIndex="10"
                bg="white"
                boxShadow="md"
                borderRadius="md"
                p="4"
                mt="2"
                width="350px"
                transform="translateY(140px)"
              >
                <CategoryFilter
                  selectedCategories={filters.selectedCategories}
                  onCategoryChange={handleCategoryFilterChange}
                  onClose={() => setShowCategoryFilter(false)}
                />
              </Box>
            )}
            <Input
              placeholder="Мінімальна ціна"
              name="minPrice"
              type="number"
              value={filters.minPrice}
              onChange={(e) => setFilters({ ...filters, minPrice: e.target.value })}
              width="200px"
            />
            <Input
              placeholder="Максимальна ціна"
              name="maxPrice"
              type="number"
              value={filters.maxPrice}
              onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })}
              width="200px"
            />
            <Button onClick={clearFilters} colorScheme="red" variant="outline">
              Очистити фільтри
            </Button>
          </HStack>
        </VStack>
      </Box>

      <Divider mb="6" />

      {/* Product List Section */}
      <Box display="flex" alignItems="flex-start">
        <Box flex="1">
          {loading ? (
            <Spinner />
          ) : (
            <Box>
              {products.length ? (
                products.map((product) => (
                  <Box
                    key={product.id}
                    p="4"
                    shadow="sm"
                    borderWidth="1px"
                    borderRadius="md"
                    mb="2"
                    bg="white"
                    onClick={() => handleProductClick(product)}
                  >
                    <Flex align="center" justify="space-between">
                      <Flex align="center">
                        <Box boxSize="80px" borderRadius="md" overflow="hidden" mr="4">
                          <Image
                            src={product.images[0]?.image || placeholderImage}
                            alt={product.name}
                            boxSize="100%"
                            objectFit="cover"
                          />
                        </Box>
                        <Box>
                          <HStack spacing="2" align="baseline">
                            <Text fontSize="xs" color="gray.500">ID: {product.id}</Text>
                            <Text fontSize="md" fontWeight="bold" isTruncated>
                              {product.name}
                            </Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.600" isTruncated>
                            Каталожний номер: {product.catalog_number}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            Кількість: {product.stock_quantity} {product.unit_measurement}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            Ціна: {Number(product.price).toFixed(2)} грн
                          </Text>
                          {product.is_vat && (
                            <Text fontSize="sm" color="blue.600">
                              Ціна з ПДВ: {Number(product.price_with_vat).toFixed(2)} грн
                            </Text>
                          )}
                        </Box>
                      </Flex>
                      <Flex>
                        <IconButton
                          icon={<EditIcon />}
                          aria-label="Edit product"
                          onClick={(event) => handleOpenEditProduct(product.id, event)}
                          mr="2"
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          aria-label="Delete product"
                          onClick={(event) => handleOpenDeleteConfirm(product, event)}
                          colorScheme="red"
                        />
                      </Flex>
                    </Flex>
                  </Box>
                ))
              ) : (
                <Text>Не знайдено продуктів з вказаними фільтрами.</Text>
              )}
            </Box>
          )}

          {/* Pagination */}
          {pagination.count > 0 && (
            <Flex justify="center" mt="6" align="center">
              {pagination.count > 10 && ( // Only show pagination if there are more than 12 items
                <HStack spacing="2">
                  {/* Previous Button */}
                  <Button
                    size="sm"
                    onClick={() => {
                      fetchPrevious();
                    }}
                    isDisabled={!pagination.previous}
                  >
                    Попередня
                  </Button>
                  
                  {/* Dynamic Page Numbers */}
                  {Array.from({ length: Math.ceil(pagination.count / 10) }, (_, index) => (
                    <Button
                      key={index + 1}
                      size="sm"
                      colorScheme={pagination.currentPage === index + 1 ? 'blue' : 'gray'}
                      onClick={() => {
                        goToPage(index + 1); // Move to selected page
                      }}
                    >
                      {index + 1}
                    </Button>
                  ))}
                  
                  {/* Next Button */}
                  <Button
                    size="sm"
                    onClick={() => {
                      fetchNext();
                    }}
                    isDisabled={!pagination.next}
                  >
                    Наступна
                  </Button>
                </HStack>
              )}
            </Flex>
          )}
        </Box>

        {/* Sidebar */}
        {selectedProduct && (
          <ProductSidebar
            product={selectedProduct}
            currentImageIndex={currentImageIndex}
            setCurrentImageIndex={setCurrentImageIndex}
            onClose={handleCloseSidebar}
            isImagePopupOpen={isImagePopupOpen}
            openImagePopup={openImagePopup}
            closeImagePopup={closeImagePopup}
            placeholderImage={placeholderImage}
          />
        )}
      </Box>

      {/* Delete Confirmation Modal */}
      {isDeleteConfirmOpen && (
        <Modal isOpen={isDeleteConfirmOpen} onClose={handleCloseDeleteConfirm}>
          <ModalOverlay />
          <ModalContent>
            <ModalBody p="6">
              <Text fontSize="md" mb="4">
                Ви впевнені, що хочете видалити цей товар?
              </Text>
              <Flex justify="flex-end">
                <Button onClick={handleCloseDeleteConfirm} mr="3">
                  Скасувати
                </Button>
                <Button colorScheme="red" onClick={handleDeleteProduct}>
                  Видалити
                </Button>
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default Products;
